import * as React from 'react';
import { Platform } from 'react-native';
import styled from 'styled-components/native';
import { Theme } from '../../theme';

const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

type TurnstileOptions = {
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback": () => void;
  "error-callback": () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  reset: (widgetId?: string) => void;
  remove?: (widgetId?: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript() {
  if (window.turnstile) {
    return Promise.resolve();
  }

  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Could not load Turnstile')));
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Could not load Turnstile'));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
}

const CaptchaRow = styled.View`
  padding: ${(props: { theme: Theme }) => props.theme.spacing.md};
  border-bottom-color: ${(props: { theme: Theme }) => props.theme.palette.lineColor};
  border-bottom-width: 1px;
  align-items: center;
  min-height: 90px;
  justify-content: center;
`;

export function TurnstileCaptcha(props: { siteKey: string; resetKey: number; onTokenChange: (token: string) => void }) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const widgetIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (Platform.OS !== 'web' || !props.siteKey) {
      return;
    }

    let cancelled = false;
    props.onTokenChange('');

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) {
          return;
        }

        if (widgetIdRef.current && window.turnstile.remove) {
          window.turnstile.remove(widgetIdRef.current);
        }
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: props.siteKey,
          callback: props.onTokenChange,
          "expired-callback": () => props.onTokenChange(''),
          "error-callback": () => props.onTokenChange(''),
        });
      })
      .catch(() => props.onTokenChange(''));

    return () => {
      cancelled = true;
      if (!widgetIdRef.current || !window.turnstile) {
        return;
      }

      if (window.turnstile.remove) {
        window.turnstile.remove(widgetIdRef.current);
      } else {
        window.turnstile.reset(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [props.siteKey, props.resetKey, props.onTokenChange]);

  if (Platform.OS !== 'web' || !props.siteKey) {
    return null;
  }

  return (
    <CaptchaRow>
      {React.createElement('div', { ref: containerRef })}
    </CaptchaRow>
  );
}
